import Phaser from 'phaser';

// Friendly "space-shooter" reticle: a ring with four little triangles pointing inward
// toward a center dot — the kind of playful targeting cursor used in kids' games.
const COLOR = 0xff3030;
const RING_RADIUS = 22;
const INNER_DOT = 2.5;
const STROKE = 3;
const TRI_INNER = 12;    // apex distance from center (the inward-pointing tip)
const TRI_OUTER = 22;    // base distance from center (meets the ring)
const TRI_HALF = 7;      // half-width of each triangle's base
/** One reticle texture for the whole session — see the constructor. */
const TEXTURE_KEY = 'crosshair-reticle';

export class Crosshair extends Phaser.GameObjects.Container {
  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0);
    this.setDepth(10_000);

    // Drawn once into a texture, then worn as a sprite. The shape is a ring, a dot and four
    // triangles, and it is drawn with a single Graphics (rather than Phaser's Shape objects, which
    // re-center on their bounding box) so the asymmetric triangles land at exact local coordinates.
    //
    // It has to be a TEXTURE because this object sits on screen for the whole board: a retained
    // Graphics is re-tessellated every frame by Phaser, and `strokeCircle` alone is ~100 points of
    // that (`iterStep` 0.01 in GraphicsWebGLRenderer). Baked once and shared by every board of the
    // session — the reticle is the same 50 pixels each time.
    if (!scene.textures.exists(TEXTURE_KEY)) {
      const half = RING_RADIUS + STROKE;
      const gfx = scene.make.graphics({ x: 0, y: 0 }, false);
      gfx.translateCanvas(half, half);
      gfx.lineStyle(STROKE, COLOR, 1);
      gfx.strokeCircle(0, 0, RING_RADIUS);
      gfx.fillStyle(COLOR, 1);
      gfx.fillCircle(0, 0, INNER_DOT);

      // Four triangles at N/E/S/W, each apex (TRI_INNER) pointing toward the center.
      const i = TRI_INNER, o = TRI_OUTER, hw = TRI_HALF;
      gfx.fillTriangle(0, -i, -hw, -o,  hw, -o);   // top → tip points down (inward)
      gfx.fillTriangle(0,  i, -hw,  o,  hw,  o);   // bottom → tip points up
      gfx.fillTriangle(-i, 0, -o, -hw, -o,  hw);   // left → tip points right
      gfx.fillTriangle( i, 0,  o, -hw,  o,  hw);   // right → tip points left

      gfx.generateTexture(TEXTURE_KEY, half * 2, half * 2);
      gfx.destroy();
    }

    this.add(scene.add.image(0, 0, TEXTURE_KEY));
    scene.add.existing(this);

    // Follow the shared active pointer every frame. We derive the world point from the pointer's
    // SCREEN coords (p.x/p.y), which the input manager updates globally on every move, rather than
    // p.worldX/worldY — those are camera-relative and only refresh when *this* scene processes the
    // pointer, which is skipped while the cursor is over an interactive object in a scene above
    // (e.g. the HUD's "← Menu" button). Using worldX there froze the crosshair until you moved off.
    const cam = scene.cameras.main;
    const wp = new Phaser.Math.Vector2();
    const follow = () => {
      const p = scene.input.activePointer;
      cam.getWorldPoint(p.x, p.y, wp);
      this.setPosition(wp.x, wp.y);
    };
    follow();   // start at the current pointer (board is entered via a click, not a move)
    scene.events.on(Phaser.Scenes.Events.UPDATE, follow);
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      scene.events.off(Phaser.Scenes.Events.UPDATE, follow);
    });
  }
}
